<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import Report from 'dashboard/api/reports';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';

const { t } = useI18n();
const store = useStore();
const { uiSettings } = useUISettings();

const availableLabels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getInboxes');
const currentAccountId = useMapGetter('getCurrentAccountId');

// Persist the funnel order PER ACCOUNT so the config never leaks between
// accounts (ui_settings are stored per-user/global, so we scope the key).
const funnelStorageKey = computed(
  () => `label_funnel_labels_${currentAccountId.value}`
);

// Ordered list of label titles that make up the funnel (stage 1, 2, 3, ...).
const funnelLabels = ref([]);
const selectedInboxId = ref('');
const dateRange = ref({
  from: getUnixStartOfDay(subDays(new Date(), 29)),
  to: getUnixEndOfDay(new Date()),
});
const funnelData = ref([]);
const isLoading = ref(false);

const labelByTitle = computed(() => {
  const map = {};
  availableLabels.value.forEach(label => {
    map[label.title] = label;
  });
  return map;
});

const colorFor = title => labelByTitle.value[title]?.color || '#94a3b8';

const maxCount = computed(() =>
  Math.max(1, ...funnelData.value.map(stage => stage.count || 0))
);

// Funnel geometry: each stage is a trapezoid whose top width maps to this
// stage's count and bottom width to the next stage's count, so it narrows.
// Labels sit to the left and numbers to the right (outside the funnel), so the
// coloured band in the middle can narrow freely.
const MIN_BAND = 0.06;
const funnelStages = computed(() => {
  const total = funnelData.value.length;
  return funnelData.value.map((stage, index) => {
    const topFraction = (stage.count || 0) / maxCount.value;
    const next = funnelData.value[index + 1];
    const bottomFraction =
      index === total - 1 ? topFraction : (next?.count || 0) / maxCount.value;
    return {
      ...stage,
      color: colorFor(stage.label),
      topWidth: Math.max(topFraction, MIN_BAND) * 100,
      bottomWidth: Math.max(bottomFraction, MIN_BAND) * 100,
    };
  });
});

const fetchFunnel = async () => {
  if (!funnelLabels.value.length) {
    funnelData.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await Report.getLabelFunnel({
      labels: funnelLabels.value,
      from: dateRange.value.from,
      to: dateRange.value.to,
      inboxId: selectedInboxId.value || undefined,
    });
    funnelData.value = data || [];
  } catch (error) {
    funnelData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Pie: distribution of conversations across every stage.
const polarToCartesian = (cx, cy, r, angleDeg) => {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const pieTotal = computed(() =>
  funnelData.value.reduce((sum, s) => sum + (s.count || 0), 0)
);
const pieSlices = computed(() => {
  const total = pieTotal.value || 1;
  let acc = 0;
  return funnelData.value.map(stage => {
    const frac = (stage.count || 0) / total;
    let d;
    if (frac >= 0.9999) {
      // A single 100% slice: draw a full circle (two arcs).
      d = 'M21,1 A20,20 0 1 1 21,41 A20,20 0 1 1 21,1 Z';
    } else {
      const startA = acc * 360;
      const endA = (acc + frac) * 360;
      const [x1, y1] = polarToCartesian(21, 21, 20, startA);
      const [x2, y2] = polarToCartesian(21, 21, 20, endA);
      const large = endA - startA > 180 ? 1 : 0;
      d = `M21,21 L${x1.toFixed(3)},${y1.toFixed(3)} A20,20 0 ${large} 1 ${x2.toFixed(3)},${y2.toFixed(3)} Z`;
    }
    acc += frac;
    return {
      label: stage.label,
      color: colorFor(stage.label),
      pct: Math.round(frac * 100),
      count: stage.count || 0,
      d,
    };
  });
});

// Daily chart: conversations created per day, stacked per label so each day is
// a coloured bar showing how many landed in each tag.
const dailyData = ref([]);

const fetchDaily = async () => {
  if (!funnelLabels.value.length) {
    dailyData.value = [];
    return;
  }
  try {
    const { data } = await Report.getLabelFunnelDaily({
      labels: funnelLabels.value,
      from: dateRange.value.from,
      to: dateRange.value.to,
      inboxId: selectedInboxId.value || undefined,
    });
    dailyData.value = data || [];
  } catch (error) {
    dailyData.value = [];
  }
};

const maxDaily = computed(() =>
  Math.max(1, ...dailyData.value.map(d => d.total || 0))
);
const totalDaily = computed(() =>
  dailyData.value.reduce((sum, d) => sum + (d.total || 0), 0)
);
// Build each day column as a stack of coloured segments (one per label).
const dailyColumns = computed(() =>
  dailyData.value.map(day => ({
    date: day.date,
    total: day.total || 0,
    segments: funnelLabels.value
      .map(label => ({
        label,
        count: (day.by_label && day.by_label[label]) || 0,
        color: colorFor(label),
      }))
      .filter(seg => seg.count > 0),
  }))
);
// Only label some days on the x-axis when the range is long, to avoid clutter.
const dailyLabelEvery = computed(() => {
  const n = dailyData.value.length;
  if (n <= 10) return 1;
  if (n <= 20) return 2;
  if (n <= 45) return 5;
  return 7;
});
const dayNumber = dateStr => Number(dateStr.slice(8, 10));
const dailyTooltip = day => {
  const [y, m, d] = day.date.split('-');
  const header = `${d}/${m}/${y} — ${t('LABEL_FUNNEL.DAILY_TOTAL')}: ${day.total}`;
  const lines = day.segments.map(seg => `${seg.label}: ${seg.count}`);
  return [header, ...lines].join('\n');
};

// Excel export: a real 2-sheet workbook (SpreadsheetML 2003) so Excel opens it
// with a "Dados" tab (summary + mini bar chart) and a "Contatos" tab.
const isExporting = ref(false);
const xmlEsc = value => {
  const str = value === null || value === undefined ? '' : String(value);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};
const triggerDownload = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const strCell = (value, styleId) =>
  `<Cell${styleId ? ` ss:StyleID="${styleId}"` : ''}><Data ss:Type="String">${xmlEsc(value)}</Data></Cell>`;
const numCell = (value, styleId) =>
  `<Cell${styleId ? ` ss:StyleID="${styleId}"` : ''}><Data ss:Type="Number">${Number(value) || 0}</Data></Cell>`;
const headerCells = cols => cols.map(c => strCell(c, 'hdr')).join('');
const exportExcel = async () => {
  if (!funnelLabels.value.length) return;
  isExporting.value = true;
  try {
    const { data } = await Report.getLabelFunnelContacts({
      labels: funnelLabels.value,
      from: dateRange.value.from,
      to: dateRange.value.to,
      inboxId: selectedInboxId.value || undefined,
    });

    // One coloured style per stage, so the "Etapa" cell keeps the tag colour.
    const stageStyles = funnelData.value
      .map((s, i) => {
        const color = (colorFor(s.label) || '#64748b').slice(0, 7);
        return `<Style ss:ID="st${i}"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="${color}" ss:Pattern="Solid"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#FFFFFF"/></Borders></Style>`;
      })
      .join('');

    const summaryRows = funnelData.value
      .map((s, i) => {
        const blocks = '█'.repeat(Math.round((s.overall_percentage || 0) / 5));
        return `<Row>${strCell(s.label, `st${i}`)}${numCell(s.count)}${strCell(`${s.overall_percentage}%`, 'ctr')}${strCell(`${s.step_percentage}%`, 'ctr')}${strCell(`${blocks} ${s.overall_percentage}%`, 'bar')}</Row>`;
      })
      .join('');

    const contactRows = (data || [])
      .map(
        c =>
          `<Row>${strCell(c.stage)}${strCell(c.contact_name)}${strCell(c.phone_number)}${strCell(c.email)}${strCell(`#${c.conversation_id}`)}</Row>`
      )
      .join('');

    const dataHeader = headerCells([
      t('LABEL_FUNNEL.CSV.STAGE'),
      t('LABEL_FUNNEL.CSV.COUNT'),
      t('LABEL_FUNNEL.OVERALL'),
      t('LABEL_FUNNEL.STEP'),
      t('LABEL_FUNNEL.EXCEL.CHART'),
    ]);
    const contactsHeader = headerCells([
      t('LABEL_FUNNEL.CSV.STAGE'),
      t('LABEL_FUNNEL.CSV.NAME'),
      t('LABEL_FUNNEL.CSV.PHONE'),
      t('LABEL_FUNNEL.CSV.EMAIL'),
      t('LABEL_FUNNEL.CSV.CONVERSATION'),
    ]);

    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Center"/><Font ss:FontName="Calibri" ss:Size="11" ss:Color="#1F2937"/></Style>
  <Style ss:ID="hdr"><Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11"/><Interior ss:Color="#3B0A91" ss:Pattern="Solid"/><Alignment ss:Horizontal="Left" ss:Vertical="Center"/></Style>
  <Style ss:ID="ctr"><Alignment ss:Horizontal="Center" ss:Vertical="Center"/></Style>
  <Style ss:ID="bar"><Font ss:Color="#3B0A91"/></Style>
  ${stageStyles}
 </Styles>
 <Worksheet ss:Name="${xmlEsc(t('LABEL_FUNNEL.EXCEL.SHEET_DATA'))}">
  <Table>
   <Column ss:Width="200"/><Column ss:Width="80"/><Column ss:Width="110"/><Column ss:Width="130"/><Column ss:Width="180"/>
   <Row>${dataHeader}</Row>
   ${summaryRows}
  </Table>
 </Worksheet>
 <Worksheet ss:Name="${xmlEsc(t('LABEL_FUNNEL.EXCEL.SHEET_CONTACTS'))}">
  <Table>
   <Column ss:Width="160"/><Column ss:Width="240"/><Column ss:Width="140"/><Column ss:Width="200"/><Column ss:Width="90"/>
   <Row>${contactsHeader}</Row>
   ${contactRows}
  </Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob(['﻿', xml], {
      type: 'application/vnd.ms-excel;charset=utf-8',
    });
    triggerDownload(`funil-etiquetas-${Date.now()}.xls`, blob);
  } catch (error) {
    useAlert(t('LABEL_FUNNEL.EXPORT_ERROR'));
  } finally {
    isExporting.value = false;
  }
};

const onDateRangeChange = event => {
  const value = event.target.value;
  if (!value) return;
  const [year, month, day] = value.split('-').map(Number);
  const picked = new Date(year, month - 1, day);
  if (event.target.name === 'from') {
    dateRange.value.from = getUnixStartOfDay(picked);
  } else {
    dateRange.value.to = getUnixEndOfDay(picked);
  }
  fetchFunnel();
  fetchDaily();
};

const onFiltersChange = () => {
  fetchFunnel();
  fetchDaily();
};

const toDateInput = unix => {
  const d = new Date(unix * 1000);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Real-time: refresh the tracking numbers every 30s.
const REFRESH_MS = 30000;
let refreshTimer = null;


const refreshAll = () => {
  fetchFunnel();
  fetchDaily();
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  fetchDaily();
  refreshTimer = setInterval(refreshAll, REFRESH_MS);
});

onBeforeUnmount(() => {
  if (refreshTimer) clearInterval(refreshTimer);
});

// Restore the saved funnel order once UI settings are available.
watch(
  () => uiSettings.value?.[funnelStorageKey.value],
  saved => {
    if (saved && !funnelLabels.value.length) {
      funnelLabels.value = [...saved];
      fetchFunnel();
      fetchDaily();
    }
  },
  { immediate: true }
);
</script>

<template>
  <ReportHeader :header-title="t('LABEL_FUNNEL.HEADER')" />

  <div class="flex flex-col gap-4 px-1 pb-8">
    <!-- Filters -->
    <div
      class="flex flex-wrap items-end gap-4 p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('LABEL_FUNNEL.FROM') }}
        </span>
        <input
          name="from"
          type="date"
          :value="toDateInput(dateRange.from)"
          class="reset-base h-9 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
          @change="onDateRangeChange"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('LABEL_FUNNEL.TO') }}
        </span>
        <input
          name="to"
          type="date"
          :value="toDateInput(dateRange.to)"
          class="reset-base h-9 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none"
          @change="onDateRangeChange"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('LABEL_FUNNEL.CHANNEL') }}
        </span>
        <select
          v-model="selectedInboxId"
          class="reset-base h-9 px-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 focus:outline-none min-w-[10rem]"
          @change="onFiltersChange"
        >
          <option value="">{{ t('LABEL_FUNNEL.ALL_CHANNELS') }}</option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </label>

      <span
        class="flex items-center gap-1.5 ml-auto text-xs font-medium text-n-teal-11"
      >
        <span class="relative flex w-2 h-2">
          <span
            class="absolute inline-flex w-full h-full rounded-full opacity-75 bg-n-teal-9 animate-ping"
          />
          <span class="relative inline-flex w-2 h-2 rounded-full bg-n-teal-9" />
        </span>
        {{ t('LABEL_FUNNEL.REAL_TIME') }}
      </span>
    </div>

    <!-- KPI cards: one per stage, updated in real time -->
    <div
      v-if="funnelData.length"
      class="grid gap-3"
      :style="{
        gridTemplateColumns: `repeat(auto-fit, minmax(9rem, 1fr))`,
      }"
    >
      <div
        v-for="stage in funnelData"
        :key="`kpi-${stage.label}`"
        class="flex flex-col gap-1 p-3 rounded-xl bg-n-solid-2 shadow outline-1 outline outline-n-container border-t-2"
        :style="{ borderTopColor: colorFor(stage.label) }"
      >
        <span class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 truncate">
          <span
            class="flex-shrink-0 w-2 h-2 rounded-full"
            :style="{ backgroundColor: colorFor(stage.label) }"
          />
          {{ stage.label }}
        </span>
        <span class="text-2xl font-semibold tabular-nums text-n-slate-12">
          {{ stage.count }}
        </span>
        <span class="text-xs tabular-nums text-n-slate-10">
          {{ stage.overall_percentage }}% {{ t('LABEL_FUNNEL.OF_TOTAL') }}
        </span>
      </div>
    </div>

    <!-- Funil + Distribuição lado a lado -->
    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <!-- Funil -->
      <div
        class="flex flex-col p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 min-h-[16rem]"
      >
        <h3 class="m-0 mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('LABEL_FUNNEL.HEADER') }}
        </h3>
      <div
        v-if="isLoading"
        class="flex items-center justify-center flex-1 text-sm text-n-slate-11"
      >
        {{ t('LABEL_FUNNEL.LOADING') }}
      </div>
      <div
        v-else-if="!funnelStages.length"
        class="flex flex-col items-center justify-center flex-1 gap-2 text-center"
      >
        <span class="i-lucide-filter size-8 text-n-slate-8" />
        <p class="m-0 text-sm text-n-slate-11">
          {{ t('LABEL_FUNNEL.CONFIGURE_FIRST') }}
        </p>
      </div>
      <div
        v-else
        class="flex items-stretch flex-1 gap-2"
        :style="{ minHeight: `${funnelStages.length * 46}px` }"
      >
        <!-- Nomes (à esquerda, fora do funil) -->
        <div class="flex flex-col flex-1 min-w-0">
          <div
            v-for="stage in funnelStages"
            :key="`ln-${stage.label}`"
            class="flex items-center justify-end flex-1 pr-1 text-right"
          >
            <span
              class="text-xs leading-tight text-n-slate-12"
              :title="stage.label"
            >
              {{ stage.label }}
            </span>
          </div>
        </div>

        <!-- Funil (centro, colorido) -->
        <svg
          :viewBox="`0 0 100 ${funnelStages.length * 16}`"
          preserveAspectRatio="none"
          class="flex-shrink-0 w-[32%] max-w-[220px]"
        >
          <polygon
            v-for="(stage, index) in funnelStages"
            :key="stage.label"
            :points="`${(100 - stage.topWidth) / 2},${index * 16 + 0.3} ${(100 + stage.topWidth) / 2},${index * 16 + 0.3} ${(100 + stage.bottomWidth) / 2},${index * 16 + 15.7} ${(100 - stage.bottomWidth) / 2},${index * 16 + 15.7}`"
            :fill="stage.color"
          />
        </svg>

        <!-- Números (à direita, fora do funil) -->
        <div class="flex flex-col flex-1 min-w-0">
          <div
            v-for="(stage, index) in funnelStages"
            :key="`rn-${stage.label}`"
            class="flex flex-col justify-center flex-1 pl-1"
          >
            <span class="text-sm font-semibold tabular-nums text-n-slate-12">
              {{ stage.count }}
            </span>
            <span class="text-[11px] tabular-nums text-n-slate-10">
              {{ stage.overall_percentage }}%<template v-if="index > 0">
                · ↓{{ stage.step_percentage }}%</template
              >
            </span>
          </div>
        </div>
      </div>
      </div>

      <!-- Distribuição -->
      <div
        v-if="funnelData.length"
        class="flex items-center gap-6 p-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <svg
          width="120"
          height="120"
          viewBox="0 0 42 42"
          class="flex-shrink-0"
        >
          <path
            v-for="slice in pieSlices"
            :key="`pie-${slice.label}`"
            :d="slice.d"
            :fill="slice.color"
          />
        </svg>
        <div class="flex flex-col gap-1.5 min-w-0">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ t('LABEL_FUNNEL.PIE_TITLE') }}
          </h3>
          <div
            v-for="slice in pieSlices"
            :key="`pl-${slice.label}`"
            class="flex items-center gap-2 text-sm"
          >
            <span
              class="flex-shrink-0 w-2.5 h-2.5 rounded-full"
              :style="{ backgroundColor: slice.color }"
            />
            <span class="flex-1 truncate text-n-slate-11">
              {{ slice.label }}
            </span>
            <span class="font-semibold tabular-nums text-n-slate-12">
              {{ slice.count }}
            </span>
            <span class="w-10 text-right tabular-nums text-n-slate-10">
              {{ slice.pct }}%
            </span>
          </div>
        </div>
      </div>

    </div>

    <!-- Conversas dia a dia no período escolhido, empilhadas por etiqueta -->
    <div
      v-if="dailyData.length"
      class="flex flex-col gap-3 p-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ t('LABEL_FUNNEL.DAILY_TITLE') }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ t('LABEL_FUNNEL.DAILY_TOTAL') }}:
          <span class="font-semibold text-n-slate-12 tabular-nums">
            {{ totalDaily }}
          </span>
        </span>
      </div>

      <!-- Legenda das etiquetas -->
      <div class="flex flex-wrap gap-x-4 gap-y-1">
        <span
          v-for="stage in funnelData"
          :key="`leg-${stage.label}`"
          class="flex items-center gap-1.5 text-xs text-n-slate-11"
        >
          <span
            class="w-2.5 h-2.5 rounded-sm"
            :style="{ backgroundColor: colorFor(stage.label) }"
          />
          {{ stage.label }}
        </span>
      </div>

      <div class="flex items-end gap-1 h-52 pt-5">
        <div
          v-for="(day, index) in dailyColumns"
          :key="day.date"
          class="relative flex flex-col items-center justify-end flex-1 h-full min-w-0 group"
          :title="dailyTooltip(day)"
        >
          <span
            class="absolute -top-4 text-[10px] font-medium tabular-nums text-n-slate-11 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {{ day.total }}
          </span>
          <!-- Barra empilhada: um segmento colorido por etiqueta -->
          <div
            class="flex flex-col-reverse w-full overflow-hidden rounded-t"
            :style="{
              height: `${(day.total / maxDaily) * 100}%`,
              minHeight: day.total > 0 ? '3px' : '0',
            }"
          >
            <div
              v-for="seg in day.segments"
              :key="`${day.date}-${seg.label}`"
              :style="{
                height: `${(seg.count / day.total) * 100}%`,
                backgroundColor: seg.color,
              }"
            />
          </div>
          <span
            v-if="index % dailyLabelEvery === 0"
            class="mt-1 text-[9px] tabular-nums text-n-slate-10"
          >
            {{ dayNumber(day.date) }}
          </span>
          <span v-else class="mt-1 text-[9px]">&nbsp;</span>
        </div>
      </div>
    </div>

    <div v-if="funnelData.length" class="flex justify-end pt-2">
      <NextButton
        :label="isExporting ? t('LABEL_FUNNEL.EXPORTING') : t('LABEL_FUNNEL.EXPORT')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        sm
        @click="exportExcel"
      />
    </div>
  </div>
</template>
